import { QueryFunctionContext, UseQueryOptions } from 'react-query';
import { ApiError, ApiMethod, ApiResponse, getRaw, postRaw } from 'services/api';
import { RestQuery } from './types';

const SECOND = 1000;
const MIN_RETRY_DELAY = 1 * SECOND;
const MAX_RETRY_DELAY = 30 * SECOND;

const PARTIAL_CONTENT_STATUS = 206;

// Helper method to handle any API responses that warrant specific actions globally
export const handleResponse = <Response>(response: ApiResponse<Response>): Response => {
  // Handle 206/PartialContent returned from caching layer
  if (response.status === 206) {
    // We make this an error so that react-query can treat the request as failed and automatically retry it
    throw new ApiError(
      'Partial Content',
      PARTIAL_CONTENT_STATUS.toString(),
      response.config,
      response.request,
      response,
    );
  }

  return response.data;
};

// Default delay
export const calculateExponentialDelay = (attempt: number): number => {
  const isFirstRetry = attempt > 1;

  const exponentialDelay = 2 ** attempt * SECOND;

  // Calculations are: 1s, 4s, 8s, 16s, 30s, 30s
  return Math.min(isFirstRetry ? exponentialDelay : MIN_RETRY_DELAY, MAX_RETRY_DELAY);
};

export const calculateLinearDelay = (attempt: number, step = 10): number => {
  // Default steps are 10s, 20s, 30s, 30s
  return Math.min(attempt * step * SECOND, MAX_RETRY_DELAY);
};

export const calculateWaveDelay = (attempt: number): number => {
  const isFirstRetry = attempt > 1;

  // linear delay for odd attempts: 20s, 40s, 60s, 80s
  const linearServiceDelay = calculateLinearDelay(Math.floor(attempt / 2) + 1, 20);

  // exponential delay for even attempts: 4s, 16s, 64s, 256s
  const exponentialServiceDelay = 2 ** attempt * SECOND;

  // Note that this DOES NOT have an upper limit and attempt delays after #5 are a minute or longer
  return isFirstRetry ? linearServiceDelay : exponentialServiceDelay;
};

// Helper method to calculate delay before retrying query
export const calculateRetryDelay = <TError>(attempt: number, error: TError): number => {
  if (error instanceof ApiError) {
    if (error.status === PARTIAL_CONTENT_STATUS) {
      return calculateLinearDelay(attempt);
    } else if (error.response?.status === 504) {
      return calculateWaveDelay(attempt);
    }
  }

  return calculateExponentialDelay(attempt);
};

/**
 * Get queries are automatically cancelled when the query is no longer required.
 * For context, see: https://iterable.atlassian.net/browse/PRO-6388
 */
const GET_QUERY =
  <Response, Params>({
    url,
    params,
    requestConfig,
    handleResponse: customResponseHandler,
  }: RestQuery<Response, Params>) =>
  (queryFn: QueryFunctionContext) => {
    if (url) {
      return getRaw<Response>(url, {
        ...requestConfig,
        params,
        signal: queryFn.signal,
      }).then(customResponseHandler ?? handleResponse);
    }
  };

export const POST_QUERY =
  <Response, Params>({
    url,
    params,
    requestConfig,
    handleResponse: customResponseHandler,
  }: RestQuery<Response, Params>) =>
  (queryFn: QueryFunctionContext) => {
    if (url) {
      return postRaw<Response>(url, params, { ...requestConfig, signal: queryFn.signal }).then(
        customResponseHandler ?? handleResponse,
      );
    }
  };

export const createQueryHookOptions = <Response, Params>(
  query: RestQuery<Response, Params>,
): UseQueryOptions<Response | undefined, ApiError> => {
  const { url, method, params, queryOptions } = query;
  const queryFn = method === ApiMethod.post ? POST_QUERY(query) : GET_QUERY(query);

  return {
    queryKey: [url, params],
    queryFn,
    retryDelay: calculateRetryDelay,
    ...queryOptions,
  };
};
